
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { InventoryTransferSuggestions } from '@/collections/inventory_transfer_suggestions';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { InventoryTransferRouteList } from '@/collections/inventory_transfer_routes';
import { InventoryTransferRouteModel } from '@/models/inventory_transfer_route';
import { InventoryTransfers } from '@/collections/inventory_transfers';
import { CreateTransferRequest } from '@/models/inventory_transfer';
import { EventBus } from '@/main';
import { StoreModel } from '@/models/store';
import { Redirect } from '@shopify/app-bridge/actions';
import { isShopifyEmbedded } from '@shopify/app-bridge/utilities';
import PriorityScoreCard from '@/components/PriorityScoreCard.vue';
import PriorityBadge from '@/components/PriorityBadge.vue';
import InventoryTransferPermissionDialog from '@/components/InventoryTransferPermissionDialog.vue';
import { EventHandler } from '@/modules/events';
import { CSVExportUtil } from '@/utils/csv-export';

@Component({
  components: {
    PriorityScoreCard,
    PriorityBadge,
    InventoryTransferPermissionDialog,
  },
})
export default class InventoryTransferSuggestionsPage extends Vue {
  @Prop({ default: null }) public app!: any;
  @Prop({ default: () => new StoreModel() }) public store!: StoreModel;
  // Data properties
  public suggestions: InventoryTransferSuggestions = new InventoryTransferSuggestions();
  public locations: StoreLocationList = new StoreLocationList();
  public routes: InventoryTransferRouteList = new InventoryTransferRouteList();
  public transfersCollection: InventoryTransfers = new InventoryTransfers();
  private _eventHandler: EventHandler | null = null;

  // Lazy initialization of EventHandler
  private get eventHandler(): EventHandler {
    if (!this._eventHandler) {
      this._eventHandler = new EventHandler({ store: this.store });
    }
    return this._eventHandler;
  }

  public loading: boolean = false;
  public reloadingAfterTransfer: boolean = false;
  public error: string | null = null;
  public selectionMode: 'source' | 'destination' = 'destination';
  public selectedLocationId: number | null = null;
  public showExplanationDialog: boolean = false;
  public selectedItem: any = null;
  public selectedRoute: any = null;
  public selectedItems: { [key: string]: any } = {};
  public expandedPriority: boolean = false;
  public priorityFilters = {
    priorityLevels: ['Critical', 'High', 'Normal', 'Low'],
  };
  public selectedVendor: string | null = null;
  public minPrice: number | null = null;
  public maxPrice: number | null = null;
  public filterPanelExpanded: number = 0; // Keep filter panel expanded by default
  public showPermissionDialog: boolean = false;
  public showTransferConfirmDialog: boolean = false;
  public confirmTransferData: any = null;
  public lastCreatedTransfer: any = null;
  public showTransferSuccessDialog: boolean = false;

  // Table headers for suggestions
  public suggestionHeaders = [
    { text: '', value: 'selection', sortable: false, width: '50px' },
    { text: 'Product Information', value: 'productInfo', sortable: false, width: '60%' },
    { text: 'Suggested Quantity', value: 'suggestedQuantity', sortable: false, width: '40%', align: 'center' },
  ];


  // Watchers for NaN normalization
  @Watch('minPrice')
  public onMinPriceChange(value: number | null): void {
    // Normalize NaN and invalid numbers to null
    if (value !== null && !Number.isFinite(value)) {
      this.$nextTick(() => {
        this.minPrice = null;
      });
    }
  }

  @Watch('maxPrice')
  public onMaxPriceChange(value: number | null): void {
    // Normalize NaN and invalid numbers to null
    if (value !== null && !Number.isFinite(value)) {
      this.$nextTick(() => {
        this.maxPrice = null;
      });
    }
  }

  // Computed properties
  public get locationMap(): Map<number, string> {
    const map = new Map<number, string>();
    this.locations.items.forEach((location: StoreLocationModel) => {
      const l = location as any;
      map.set(l.id, l.name);
    });
    return map;
  }

  public get availableLocations() {
    if (!this.routes.items || this.routes.items.length === 0) {
      return this.locations.items;
    }

    const availableLocationIds = new Set<number>();

    this.routes.items.forEach((route) => {
      const r = route as InventoryTransferRouteModel;
      if (this.selectionMode === 'destination') {
        // Show locations that can be destinations
        if (r.destinationLocationId) {
          availableLocationIds.add(r.destinationLocationId);
        }
      } else {
        // Show locations that can be sources
        if (r.sourceLocationId) {
          availableLocationIds.add(r.sourceLocationId);
        }
      }
    });

    return this.locations.items.filter((location: StoreLocationModel) => {
      const l = location as any;
      return availableLocationIds.has(l.id);
    });
  }

  public get filteredRoutes(): InventoryTransferRouteModel[] {
    if (!this.selectedLocationId) { return []; }
    const locationId = this.selectedLocationId;
    return this.routes.items.filter((route) => {
      const r = route as InventoryTransferRouteModel;
      if (this.selectionMode === 'destination') {
        return r.destinationLocationId === locationId;
      } else {
        return r.sourceLocationId === locationId;
      }
    }) as InventoryTransferRouteModel[];
  }

  public get availableVendors(): string[] {
    const vendors = new Set<string>();
    // Use priorityFilteredBase instead of filteredSuggestions to avoid circular dependency
    // This ensures only vendors with items after priority filtering are shown
    this.priorityFilteredBase.forEach((suggestion) => {
      suggestion.suggestions.forEach((item: any) => {
        if (item.vendor && item.vendor.trim() !== '') {
          vendors.add(item.vendor);
        }
      });
    });
    return Array.from(vendors).sort();
  }

  public get activeFilterCount(): number {
    let count = 0;
    if (this.selectedVendor) { count++; }
    if (Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice)) { count++; }
    return count;
  }

  public get totalActiveFilterCount(): number {
    let count = 0;
    // Count priority filter as active if not all levels are selected
    if (this.hasPriorityFilter) { count++; }
    // Count vendor filter
    if (this.selectedVendor) { count++; }
    // Count price filter (min or max)
    if (Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice)) { count++; }
    return count;
  }

  public get hasAnyActiveFilter(): boolean {
    return this.hasPriorityFilter || this.selectedVendor !== null ||
           Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice);
  }

  public get hasPriorityFilter(): boolean {
    // Priority filter is active if not all levels are selected
    return this.priorityFilters.priorityLevels.length < 4;
  }

  public get priorityFilterSummary(): string {
    const levels = this.priorityFilters.priorityLevels;
    if (levels.length === 0) { return 'None'; }
    // Show all selected levels joined by comma
    return levels.join(', ');
  }

  public get priorityLevelOptions() {
    return [
      { value: 'Critical', label: 'Critical', color: '#f6687b', icon: 'mdi-alert-octagon' },
      { value: 'High', label: 'High', color: '#faaf3b', icon: 'mdi-alert-triangle' },
      { value: 'Normal', label: 'Normal', color: '#59C8E4', icon: 'mdi-information' },
      { value: 'Low', label: 'Low', color: '#99be50', icon: 'mdi-check-circle' },
    ];
  }

  public get priceRangeText(): string {
    const hasMinPrice = Number.isFinite(this.minPrice);
    const hasMaxPrice = Number.isFinite(this.maxPrice);

    if (hasMinPrice && hasMaxPrice) {
      return `$${this.minPrice} - $${this.maxPrice}`;
    } else if (hasMinPrice) {
      return `≥ $${this.minPrice}`;
    } else if (hasMaxPrice) {
      return `≤ $${this.maxPrice}`;
    }
    return '—'; // Em dash for empty/invalid values
  }

  public get filteredSuggestions() {
    if (!this.selectedLocationId) {
      return [];
    }

    let filtered;
    if (this.selectionMode === 'source') {
      filtered = this.suggestions.items.filter((s) => {
        return Number(s.fromLocationId) === Number(this.selectedLocationId);
      });
    } else {
      filtered = this.suggestions.items.filter((s) => {
        return Number(s.toLocationId) === Number(this.selectedLocationId);
      });
    }

    return filtered;
  }

  public get priorityFilteredBase() {
    // Apply location + priority filters only (NOT vendor or price filters)
    // This is used to build the availableVendors list without circular dependency
    const baseSuggestions = this.filteredSuggestions;

    return baseSuggestions.map((suggestion) => {
      // Filter individual suggestion items based on priority criteria only
      const filteredItems = suggestion.suggestions.filter((item: any) => {
        const priorityScore = item.priorityScore || 0;
        const priorityLevel = this.getPriorityLevel(priorityScore);

        // Check priority level filter only
        if (!this.priorityFilters.priorityLevels.includes(priorityLevel)) {
          return false;
        }

        return true;
      });

      return {
        ...suggestion,
        suggestions: filteredItems,
      };
    }).filter((suggestion) => suggestion.suggestions.length > 0); // Only show routes with items
  }

  public get priorityFilteredSuggestions() {
    // Now apply vendor and price filters on top of priority-filtered base
    const baseSuggestions = this.priorityFilteredBase;

    return baseSuggestions.map((suggestion) => {
      // Filter individual suggestion items based on vendor and price only
      // (priority filtering is already done in priorityFilteredBase)
      const filteredItems = suggestion.suggestions.filter((item: any) => {
        // Check vendor filter
        if (this.selectedVendor && item.vendor !== this.selectedVendor) {
          return false;
        }

        // Check price range filter
        const priceFiltersActive = Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice);

        if (priceFiltersActive) {
          // If price filters are active, validate that unitPrice is a finite number
          const hasValidPrice = typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice);

          if (!hasValidPrice) {
            // Exclude items without valid prices when price filters are active
            return false;
          }

          // Now safely compare with price filters
          if (Number.isFinite(this.minPrice) && item.unitPrice < this.minPrice!) {
            return false;
          }
          if (Number.isFinite(this.maxPrice) && item.unitPrice > this.maxPrice!) {
            return false;
          }
        }
        // If no price filters are active, allow items with missing/non-numeric unitPrice

        return true;
      });

      return {
        ...suggestion,
        suggestions: filteredItems,
      };
    }).filter((suggestion) => suggestion.suggestions.length > 0); // Only show routes with items
  }

  public get expandedPanels(): number[] {
    // Return array of all panel indices to open them by default
    return Array.from({ length: this.priorityFilteredSuggestions.length }, (_, i) => i);
  }

  public get totalQuantity(): number {
    if (!this.confirmTransferData || !this.confirmTransferData.items) { return 0; }
    return this.confirmTransferData.items.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
  }

  public get isTransferValid(): boolean {
    if (!this.confirmTransferData || !this.confirmTransferData.items || this.confirmTransferData.items.length === 0) {
      return false;
    }

    return this.confirmTransferData.items.every((item: any) =>
      item.quantity &&
      item.quantity > 0 &&
      item.quantity <= item.sourceInventory,
    );
  }

  // Methods

  // Priority methods
  public getPriorityLevel(score: number): string {
    if (score >= 80) { return 'Critical'; }
    if (score >= 60) { return 'High'; }
    if (score >= 30) { return 'Normal'; }
    return 'Low';
  }

  public getPriorityLevelClass(score: number): string {
    const level = this.getPriorityLevel(score);
    return `priority-${level.toLowerCase()}`;
  }

  public getPriorityColor(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical': return '#f6687b';
      case 'high': return '#faaf3b';
      case 'normal': return '#59C8E4';
      case 'low': return '#99be50';
      default: return '#59C8E4';
    }
  }

  public getPriorityDescription(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical':
        return 'Immediate action required. High risk of stockout with significant revenue impact.';
      case 'high':
        return 'Urgent attention needed. Elevated risk levels require prompt transfer processing.';
      case 'normal':
        return 'Standard priority. Transfer should be processed in normal workflow.';
      case 'low':
        return 'Low urgency. Can be scheduled with other routine transfers.';
      default:
        return 'Standard priority transfer.';
    }
  }

  public getPriorityIcon(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical': return 'mdi-alert-octagon';
      case 'high': return 'mdi-alert-triangle';
      case 'normal': return 'mdi-information';
      case 'low': return 'mdi-check-circle';
      default: return 'mdi-information';
    }
  }

  // Filter methods
  public clearVendorPriceFilters(): void {
    this.selectedVendor = null;
    this.minPrice = null;
    this.maxPrice = null;
  }

  public clearAllFilters(): void {
    this.resetPriorityFilters();
    this.clearVendorPriceFilters();
  }

  public resetPriorityFilters(): void {
    this.priorityFilters.priorityLevels = ['Critical', 'High', 'Normal', 'Low'];
  }

  public togglePriorityLevel(level: string): void {
    const index = this.priorityFilters.priorityLevels.indexOf(level);
    if (index > -1) {
      this.priorityFilters.priorityLevels.splice(index, 1);
    } else {
      this.priorityFilters.priorityLevels.push(level);
    }
  }

  public showOnlyCritical(): void {
    this.priorityFilters.priorityLevels = ['Critical'];
  }

  public showHighPriority(): void {
    this.priorityFilters.priorityLevels = ['Critical', 'High'];
  }

  public getPriorityLevelCount(level: string): number {
    let count = 0;
    this.filteredSuggestions.forEach((suggestion) => {
      suggestion.suggestions.forEach((item: any) => {
        const itemLevel = this.getPriorityLevel(item.priorityScore || 0);
        if (itemLevel === level) {
          count++;
        }
      });
    });
    return count;
  }

  // Currency formatting method
  public formatCurrency(
    value: number | string | null | undefined,
    currency?: string,
    locale?: string
  ): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return 'N/A';
    }

    // Use provided currency or fall back to store currency or USD
    const currencyCode = currency || this.store?.information?.currency || 'USD';

    // Use provided locale or fall back to browser locale or undefined
    const localeCode = locale || (typeof navigator !== 'undefined' ? navigator.language : undefined);

    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  // Priority dialog methods
  public showPriorityDetails(item: any): void {
    // Find the parent suggestion for this item to get route info
    const parentSuggestion = this.suggestions.items.find((suggestion) =>
      suggestion.suggestions.some((suggItem: any) => suggItem.variantId === item.variantId),
    );

    if (parentSuggestion) {
      // Find the corresponding route
      const route = this.routes.items.find((r) =>
        r.sourceLocationId === parentSuggestion.fromLocationId &&
        r.destinationLocationId === parentSuggestion.toLocationId,
      );

      this.selectedItem = item;
      this.selectedRoute = route;
      this.showExplanationDialog = true;
    }
  }

  // Selection methods
  public toggleItemSelection(item: any, suggestion: any): void {
    const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;

    if (this.selectedItems[itemKey]) {
      // Remove from selection
      this.$delete(this.selectedItems, itemKey);
    } else {
      // Add to selection with route context
      this.$set(this.selectedItems, itemKey, {
        ...item,
        fromLocationId: suggestion.fromLocationId,
        toLocationId: suggestion.toLocationId,
        fromLocationName: this.getLocationName(suggestion.fromLocationId),
        toLocationName: this.getLocationName(suggestion.toLocationId),
      });
    }
  }

  public isItemSelected(item: any, suggestion: any): boolean {
    const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    return !!this.selectedItems[itemKey];
  }

  public clearSelection(): void {
    this.selectedItems = {};
  }

  public get hasSelectedItems(): boolean {
    return Object.keys(this.selectedItems).length > 0;
  }

  public get selectedItemsCount(): number {
    return Object.keys(this.selectedItems).length;
  }

  public get hasSuggestions(): boolean {
    return this.filteredSuggestions && this.filteredSuggestions.length > 0;
  }


  public getSelectedItemsForRoute(suggestion: any): any[] {
    const routeKey = `${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    return Object.values(this.selectedItems).filter((item) => {
      const itemRouteKey = `${item.fromLocationId}-${item.toLocationId}`;
      return itemRouteKey === routeKey;
    });
  }

  public clearRouteSelection(suggestion: any): void {
    const routeKey = `${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    const keysToRemove = Object.keys(this.selectedItems).filter((key) => {
      const item = this.selectedItems[key];
      const itemRouteKey = `${item.fromLocationId}-${item.toLocationId}`;
      return itemRouteKey === routeKey;
    });

    keysToRemove.forEach((key) => {
      this.$delete(this.selectedItems, key);
    });
  }

  public async createTransferForRoute(suggestion: any): Promise<void> {
    // Check if the store has write_inventory_transfers permission
    if (this.store && this.store.needsInventoryTransferScope()) {
      // Show the permission dialog instead of creating the transfer
      this.showPermissionDialog = true;

      // Track permission dialog shown
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Permission Dialog Shown', {
          action: 'create_transfer',
          from_location_id: suggestion.fromLocationId,
          to_location_id: suggestion.toLocationId,
        });
      }
      return;
    }

    const selectedRouteItems = this.getSelectedItemsForRoute(suggestion);
    if (selectedRouteItems.length === 0) {
      // Track empty selection attempt
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Create Attempted With No Selection', {
          from_location_id: suggestion.fromLocationId,
          to_location_id: suggestion.toLocationId,
        });
      }
      return;
    }

    // Track transfer creation initiated
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Creation Initiated', {
        from_location_id: suggestion.fromLocationId,
        from_location_name: this.getLocationName(suggestion.fromLocationId),
        to_location_id: suggestion.toLocationId,
        to_location_name: this.getLocationName(suggestion.toLocationId),
        selected_items_count: selectedRouteItems.length,
        total_quantity: selectedRouteItems.reduce((sum: number, item: any) => sum + Number(item.suggestedQuantity || 0), 0),
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Transfer Creation Started', {
        items_count: selectedRouteItems.length,
      });
    }

    const firstItem = selectedRouteItems[0];

    // Calculate transfer-level priority from selected items
    const transferPriorityScore = Math.max(...selectedRouteItems.map((item) => item.priorityScore || 0));
    const transferPriorityLevel = this.getPriorityLevel(transferPriorityScore);

    // Prepare confirmation dialog data
    this.confirmTransferData = {
      fromLocationId: firstItem.fromLocationId,
      toLocationId: firstItem.toLocationId,
      fromLocationName: firstItem.fromLocationName,
      toLocationName: firstItem.toLocationName,
      priorityScore: transferPriorityScore,
      priorityLevel: transferPriorityLevel,
      note: `Transfer from ${firstItem.fromLocationName} to ${firstItem.toLocationName}`,
      items: selectedRouteItems.map((item) => ({
        // Core item data
        productId: item.productId,
        variantId: item.variantId,
        inventoryItemId: item.inventoryItemId,
        productTitle: item.productTitle,
        variantTitle: item.variantTitle,
        sku: item.sku,
        imageUrl: item.imageUrl,
        unitCost: item.unitCost,

        // Quantity data
        quantity: item.suggestedQuantity,
        maxQuantity: item.suggestedQuantity, // Store original suggested as max
        originalSuggested: item.suggestedQuantity,

        // Current inventory levels
        sourceInventory: item.sourceInventory || 0,
        destinationInventory: item.destinationInventory || 0,

        // Priority scores (keep for API but don't display)
        priorityScore: item.priorityScore || 0,
        stockoutRiskScore: item.stockoutRiskScore || 0,
        revenueImpactScore: item.revenueImpactScore || 0,
        leadTimeUrgencyScore: item.leadTimeUrgencyScore || 0,
      })),
      // Store the original suggestion for reference
      originalSuggestion: suggestion,
    };

    // Show confirmation dialog
    this.showTransferConfirmDialog = true;
  }

  public async confirmCreateTransfer(): Promise<void> {
    if (!this.confirmTransferData || !this.isTransferValid) { return; }

    // Track transfer confirmation
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Creation Confirmed', {
        from_location_id: this.confirmTransferData.fromLocationId,
        from_location_name: this.confirmTransferData.fromLocationName,
        to_location_id: this.confirmTransferData.toLocationId,
        to_location_name: this.confirmTransferData.toLocationName,
        items_count: this.confirmTransferData.items.length,
        total_quantity: this.totalQuantity,
        priority_level: this.confirmTransferData.priorityLevel,
        priority_score: this.confirmTransferData.priorityScore,
        has_note: Boolean(this.confirmTransferData.note),
      });
    }

    // Check if store has write_inventory_transfers permission to use Shopify API
    const useShopifyTransfer = this.store && this.store.hasInventoryTransferPermission && this.store.hasInventoryTransferPermission();

    const transferData: any = {
      source_location_id: this.confirmTransferData.fromLocationId,
      destination_location_id: this.confirmTransferData.toLocationId,
      note: this.confirmTransferData.note,
      priority_score: this.confirmTransferData.priorityScore,
      priority_level: this.confirmTransferData.priorityLevel,
      items: this.confirmTransferData.items.map((item: any) => ({
        // Use inventory_item_id for Shopify transfers, product_variant_id for local transfers
        ...(useShopifyTransfer ?
          { inventory_item_id: item.inventoryItemId } :
          { product_variant_id: item.variantId }
        ),
        quantity: item.quantity,
        note: `${item.productTitle} - ${item.variantTitle}`,
        // Include priority and product information from suggestions
        priority_score: item.priorityScore || 0,
        stockout_risk_score: item.stockoutRiskScore || 0,
        revenue_impact_score: item.revenueImpactScore || 0,
        lead_time_urgency_score: item.leadTimeUrgencyScore || 0,
        product_id: item.productId,
        product_title: item.productTitle,
        variant_title: item.variantTitle,
        sku: item.sku,
        image_url: item.imageUrl,
        unit_cost: item.unitCost,
        // Include both IDs for reference
        product_variant_id: item.variantId,
        inventory_item_id: item.inventoryItemId,
      })),
    };

    // Call appropriate API based on store permissions
    const result = useShopifyTransfer ?
      await this.transfersCollection.createShopifyTransfer(transferData) :
      await this.transfersCollection.createTransfer(transferData);

    if (result) {
      // Track successful transfer creation
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Creation Success', {
          transfer_id: (result as any).id,
          from_location_id: this.confirmTransferData.fromLocationId,
          to_location_id: this.confirmTransferData.toLocationId,
          items_count: this.confirmTransferData.items.length,
          total_quantity: this.totalQuantity,
        });

        // Track funnel completion
        this.eventHandler.trackFeatureCompleted('Inventory Transfer Suggestions', {
          transfer_created: true,
          items_count: this.confirmTransferData.items.length,
        });
      }

      // Clear selection for this route after successful creation
      this.clearRouteSelection(this.confirmTransferData.originalSuggestion);

      // Show success notification with transfer link
      const itemCount = this.confirmTransferData?.items?.length || 0;
      const useShopifyTransfer = this.store && this.store.hasInventoryTransferPermission && this.store.hasInventoryTransferPermission();


      if (useShopifyTransfer && (result as any).name) {
        // Build Shopify transfer URL
        const shopName = this.store.shopName;
        const transferResult = result as any;
        const transferUrl = `https://admin.shopify.com/store/${shopName.replace('.myshopify.com', '')}/transfers/${transferResult.shopify_transfer_id.split('/').pop()}`;

        // Show success notification with link
        this.showTransferSuccessNotification(itemCount, transferUrl, transferResult.name);
      } else {
        // Show regular success notification for local transfers
        const message = `Successfully created transfer request with ${itemCount} item${itemCount !== 1 ? 's' : ''}!`;
        EventBus.$emit('show-snackbar', {
          message,
          color: 'success',
          icon: 'mdi-check-circle',
        });
      }

      // Close the confirmation dialog
      this.showTransferConfirmDialog = false;
      this.confirmTransferData = null;

      // Reload suggestions data to reflect pending transfers
      this.reloadingAfterTransfer = true;
      try {
        await this.suggestions.fetch();
      } catch (error) {
        EventBus.$emit('show-snackbar', {
          message: 'Transfer created but failed to refresh data. Please reload the page.',
          color: 'warning',
          icon: 'mdi-alert',
        });
      } finally {
        this.reloadingAfterTransfer = false;
      }
    } else {
      // Track failed transfer creation
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Creation Failed', {
          from_location_id: this.confirmTransferData.fromLocationId,
          to_location_id: this.confirmTransferData.toLocationId,
          items_count: this.confirmTransferData.items.length,
          total_quantity: this.totalQuantity,
          error_type: 'api_error',
        });

        // Track funnel abandonment
        this.eventHandler.trackFeatureAbandoned('Inventory Transfer Suggestions', 'transfer_creation_failed', {
          items_count: this.confirmTransferData.items.length,
        });
      }

      // Show error notification
      EventBus.$emit('show-snackbar', {
        message: 'Failed to create transfer request. Please try again.',
        color: 'error',
        icon: 'mdi-alert-circle',
      });
    }
  }

  public cancelTransferConfirm(): void {
    // Track transfer cancellation
    if (this.eventHandler && this.confirmTransferData) {
      this.eventHandler.track('Inventory Transfer Creation Cancelled', {
        from_location_id: this.confirmTransferData.fromLocationId,
        to_location_id: this.confirmTransferData.toLocationId,
        items_count: this.confirmTransferData.items.length,
        total_quantity: this.totalQuantity,
        stage: 'confirmation_dialog',
      });

      // Track funnel abandonment
      this.eventHandler.trackFeatureAbandoned('Inventory Transfer Suggestions', 'user_cancelled', {
        at_stage: 'confirmation_dialog',
      });
    }

    this.showTransferConfirmDialog = false;
    this.confirmTransferData = null;
  }

  public updateQuantity(index: number, newQuantity: number): void {
    if (this.confirmTransferData && this.confirmTransferData.items[index]) {
      // Allow quantities up to source inventory, not just suggested quantity
      const maxAllowed = this.confirmTransferData.items[index].sourceInventory;
      this.confirmTransferData.items[index].quantity = Math.max(0, Math.min(newQuantity, maxAllowed));
    }
  }

  public removeItemFromTransfer(index: number): void {
    if (this.confirmTransferData && this.confirmTransferData.items) {
      this.confirmTransferData.items.splice(index, 1);
    }
  }

  public showTransferSuccessNotification(itemCount: number, transferUrl: string, transferName: string): void {
    // Check if we're in embedded mode using Shopify's proper detection
    const isEmbedded = isShopifyEmbedded();

    // Store transfer data for the dialog
    this.lastCreatedTransfer = {
      name: transferName,
      url: transferUrl,
      isEmbedded,
      itemCount,
    };

    // Show success dialog
    this.showTransferSuccessDialog = true;
  }

  public closeSuccessDialog(): void {
    this.showTransferSuccessDialog = false;
    this.lastCreatedTransfer = null;
  }

  public openTransferInShopify(): void {
    if (this.lastCreatedTransfer) {
      this.openShopifyTransfer(this.lastCreatedTransfer.url, this.lastCreatedTransfer.isEmbedded);
      this.closeSuccessDialog();
    }
  }

  public openShopifyTransfer(transferUrl: string, isEmbedded: boolean): void {
    // Track transfer link click
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Shopify Link Clicked', {
        transfer_url: transferUrl,
        is_embedded: isEmbedded,
        redirect_method: 'new_tab'
      });
    }

    if (isEmbedded) {
      // Use Shopify App Bridge for embedded apps
      if (this.app) {
        // Use REMOTE action with newContext to open in new tab
        // This is the proper way per Shopify documentation
        Redirect.create(this.app).dispatch(Redirect.Action.REMOTE, {
          url: transferUrl,
          newContext: true
        });
      } else {
        // Fallback: open in new tab using native browser API
        window.open(transferUrl, '_blank');
      }
    } else {
      // For non-embedded apps, use direct window navigation
      window.open(transferUrl, '_blank');
    }
  }

  public isAllSelectedForRoute(suggestion: any): boolean {
    const routeItems = suggestion.suggestions || [];
    const selectableItems = routeItems.filter((item: any) =>
      item.suggestedQuantity !== 0 && item.suggestedQuantity !== '0',
    );
    if (selectableItems.length === 0) { return false; }

    return selectableItems.every((item: any) => this.isItemSelected(item, suggestion));
  }

  public isPartialSelectedForRoute(suggestion: any): boolean {
    const routeItems = suggestion.suggestions || [];
    const selectableItems = routeItems.filter((item: any) =>
      item.suggestedQuantity !== 0 && item.suggestedQuantity !== '0',
    );
    if (selectableItems.length === 0) { return false; }

    const selectedCount = selectableItems.filter((item: any) => this.isItemSelected(item, suggestion)).length;
    return selectedCount > 0 && selectedCount < selectableItems.length;
  }

  public toggleSelectAllForRoute(suggestion: any): void {
    const routeItems = suggestion.suggestions || [];
    const selectableItems = routeItems.filter((item: any) =>
      item.suggestedQuantity !== 0 && item.suggestedQuantity !== '0',
    );
    const isAllSelected = this.isAllSelectedForRoute(suggestion);

    if (isAllSelected) {
      // Deselect all selectable items in this route
      selectableItems.forEach((item: any) => {
        const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;
        if (this.selectedItems[itemKey]) {
          this.$delete(this.selectedItems, itemKey);
        }
      });
    } else {
      // Select all selectable items in this route
      selectableItems.forEach((item: any) => {
        const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;
        if (!this.selectedItems[itemKey]) {
          this.$set(this.selectedItems, itemKey, {
            ...item,
            fromLocationId: suggestion.fromLocationId,
            toLocationId: suggestion.toLocationId,
            fromLocationName: this.getLocationName(suggestion.fromLocationId),
            toLocationName: this.getLocationName(suggestion.toLocationId),
          });
        }
      });
    }
  }

  public getLocationName(locationId: number): string {
    return this.locationMap.get(locationId) || `Location ${locationId}`;
  }

  public getRouteForSuggestion(suggestion: any): InventoryTransferRouteModel | null {
    return this.routes.items.find((route) => {
      const r = route as InventoryTransferRouteModel;
      return Number(r.sourceLocationId) === Number(suggestion.fromLocationId) &&
             Number(r.destinationLocationId) === Number(suggestion.toLocationId);
    }) as InventoryTransferRouteModel || null;
  }

  public openExplanationDialog(item: any): void {
    this.selectedItem = item;
    // Find the route information for this item
    const parentSuggestion = this.filteredSuggestions.find((suggestion) =>
      suggestion.suggestions.includes(item),
    );
    if (parentSuggestion) {
      this.selectedRoute = this.getRouteForSuggestion(parentSuggestion);
    }

    // Track explanation dialog opened
    if (this.eventHandler && item) {
      this.eventHandler.track('Inventory Transfer Explanation Viewed', {
        product_id: item.productId,
        variant_id: item.variantId,
        product_title: item.productTitle,
        variant_title: item.variantTitle,
        sku: item.sku,
        suggested_quantity: item.suggestedQuantity,
        priority_level: this.getPriorityLevel(item.priorityScore || 0),
        priority_score: item.priorityScore || 0,
        from_location_id: parentSuggestion?.fromLocationId,
        from_location_name: parentSuggestion ? this.getLocationName(parentSuggestion.fromLocationId) : null,
        to_location_id: parentSuggestion?.toLocationId,
        to_location_name: parentSuggestion ? this.getLocationName(parentSuggestion.toLocationId) : null,
        has_pending_transfer: item.hasPendingTransfer || false,
        pending_quantity: item.pendingTransferQuantity || 0,
      });
    }

    this.showExplanationDialog = true;
  }

  // Dialog calculation methods using real API data
  public getSourceLocationName(): string {
    const parentSuggestion = this.filteredSuggestions.find((suggestion) =>
      suggestion.suggestions.includes(this.selectedItem),
    );
    return parentSuggestion ? this.getLocationName(parentSuggestion.fromLocationId) : 'Unknown';
  }

  public getDestinationLocationName(): string {
    const parentSuggestion = this.filteredSuggestions.find((suggestion) =>
      suggestion.suggestions.includes(this.selectedItem),
    );
    return parentSuggestion ? this.getLocationName(parentSuggestion.toLocationId) : 'Unknown';
  }

  public getSelectedStrategy(): string {
    if (!this.selectedItem) { return 'Unknown'; }
    const strategy = this.selectedItem.destinationStrategy;
    if (strategy === 'FORECAST') { return 'Forecast-Based'; }
    if (strategy === 'MIN_MAX') { return 'Min/Max-Based'; }
    if (strategy === 'FIXED_QUANTITY') { return 'Fixed Quantity'; }
    return strategy || 'Forecast-Based'; // Default fallback
  }

  public getStrategyDescription(): string {
    const strategy = this.getSelectedStrategy();
    if (strategy === 'Forecast-Based') {
      return 'Uses sales forecasts to dynamically calculate reorder points and target inventory levels';
    } else if (strategy === 'Min/Max-Based') {
      return 'Uses forecasted sales with defined minimum and maximum stock day ranges';
    } else if (strategy === 'Fixed Quantity') {
      return 'Uses absolute minimum and maximum inventory quantities';
    }
    return 'Strategy-based inventory management';
  }

  public getCalculatedFDS(): number {
    if (!this.selectedItem) { return 0; }
    // Use actual destination average daily sales from API
    return Math.round((this.selectedItem.destinationAvgDailySales || 0) * 100) / 100;
  }

  public getSafetyStockDays(): number {
    if (!this.selectedItem) { return 14; }
    return this.selectedItem.destinationSafetyStockDays || 14;
  }

  public getTargetStockDays(): number {
    if (!this.selectedItem) { return 30; }
    if (this.getSelectedStrategy() === 'Min/Max-Based') {
      return this.selectedItem.destinationMaxStockDays || 30;
    }
    return this.selectedItem.destinationTargetStockDays || 30;
  }

  public getCalculatedSafetyStock(): number {
    return Math.round(this.getCalculatedFDS() * this.getSafetyStockDays() * 100) / 100;
  }

  public getCalculatedDDLT(): number {
    if (!this.selectedRoute) { return 0; }
    return Math.round(this.getCalculatedFDS() * this.selectedRoute.leadTimeDays * 100) / 100;
  }

  public getCalculatedTIL(): number {
    if (!this.selectedItem) { return 0; }
    // For FIXED_QUANTITY strategy, use max_quantity directly from API
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return Math.round((this.selectedItem.destinationMaxQuantity || 0) * 100) / 100;
    }
    // For other strategies, calculate based on FDS
    return Math.ceil(this.getCalculatedFDS() * this.getTargetStockDays());
  }

  public getCalculatedROP(): number {
    if (!this.selectedItem) { return 0; }
    // For FIXED_QUANTITY strategy, use min_quantity directly from API
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return Math.round((this.selectedItem.destinationMinQuantity || 0) * 100) / 100;
    }
    // For other strategies, use reorder point from API
    return Math.round((this.selectedItem.reorderPoint || 0) * 100) / 100;
  }

  public getSafetyStockFormula(): string {
    return `FDS × Safety Stock Days = ${this.getCalculatedFDS()} × ${this.getSafetyStockDays()}`;
  }

  public getDDLTFormula(): string {
    if (!this.selectedRoute) { return ''; }
    return `FDS × Lead Time Days = ${this.getCalculatedFDS()} × ${this.selectedRoute.leadTimeDays}`;
  }

  public getTILFormula(): string {
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return `Fixed Maximum Quantity = ${this.getCalculatedTIL()}`;
    }
    const daysLabel = this.getSelectedStrategy() === 'Forecast-Based' ? 'Target Stock Days' : 'Max Stock Days';
    return `FDS × ${daysLabel} = ${this.getCalculatedFDS()} × ${this.getTargetStockDays()}`;
  }

  public getROPFormula(): string {
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return `Fixed Minimum Quantity = ${this.getCalculatedROP()}`;
    }
    if (this.getSelectedStrategy() === 'Forecast-Based') {
      return `DDLT + SS = ${this.getCalculatedDDLT()} + ${this.getCalculatedSafetyStock()}`;
    } else {
      const minDays = this.selectedItem?.destinationMinStockDays || this.selectedItem?.minStockDays || 7;
      return `FDS × Min Stock Days = ${this.getCalculatedFDS()} × ${minDays}`;
    }
  }

  public isTriggered(): boolean {
    if (!this.selectedItem) { return false; }
    return this.selectedItem.destinationInventory <= this.selectedItem.reorderPoint;
  }

  public getRouteSummary(route: InventoryTransferRouteModel) {
    if (!this.suggestions.items || this.suggestions.items.length === 0) {
      return {
        variantCount: 0,
        totalQuantity: 0,
      };
    }

    const routeSuggestions = this.suggestions.items.filter((s) => {
      if (this.selectionMode === 'destination') {
        return Number(s.fromLocationId) === Number(route.sourceLocationId) &&
               Number(s.toLocationId) === Number(route.destinationLocationId);
      } else {
        return Number(s.fromLocationId) === Number(route.sourceLocationId) &&
               Number(s.toLocationId) === Number(route.destinationLocationId);
      }
    });

    let totalVariants = 0;
    let totalQuantity = 0;

    routeSuggestions.forEach((suggestion) => {
      if (suggestion.suggestions && Array.isArray(suggestion.suggestions)) {
        totalVariants += suggestion.suggestions.length;
        suggestion.suggestions.forEach((item) => {
          const quantity = parseFloat(String(item.suggestedQuantity)) || 0;
          totalQuantity += quantity;
        });
      }
    });

    const summary = {
      variantCount: totalVariants,
      totalQuantity: Math.round(totalQuantity * 100) / 100,
    };

    return summary;
  }

  public clearLocationSelection(): void {
    this.selectedLocationId = null;
  }

  public onLocationSelected(locationId: number): void {

    // Track location selection
    if (this.eventHandler && locationId) {
      const locationName = this.getLocationName(locationId);
      this.eventHandler.track('Inventory Transfer Location Selected', {
        location_id: locationId,
        location_name: locationName,
        selection_mode: this.selectionMode,
        available_routes: this.filteredRoutes.length,
        total_suggestions: this.filteredSuggestions.length,
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Location Selected', {
        location_id: locationId,
        mode: this.selectionMode,
      });
    }
  }

  public redirectToSettings(): void {
    this.$router.push('/settings/internal-inventory-transfer-settings');
  }

  public trackHelpGuideClick(): void {
    // Track help guide click
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Help Guide Clicked', {
        page: 'inventory_transfer_suggestions',
        help_url: 'https://help.assisty.ai/en/article/inventory-transfer-suggestions-user-guide-rodpb4/',
        user_context: {
          has_selected_location: Boolean(this.selectedLocationId),
          selection_mode: this.selectionMode,
          total_routes: this.routes.items.length,
          total_suggestions: this.filteredSuggestions.length
        }
      });
    }
  }

  public setSelectionMode(mode: 'source' | 'destination'): void {

    // Track mode selection
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Mode Selected', {
        mode,
        previous_mode: this.selectionMode,
        available_locations: this.availableLocations.length,
      });
    }

    this.selectionMode = mode;
    // Clear selected location when changing mode since available locations might change
    this.selectedLocationId = null;
  }

  public customFilter(item: any, queryText: string, itemText: string): boolean {
    const searchText = queryText.toLowerCase();
    const locationName = (item.name || '').toLowerCase();
    const locationId = (item.id || '').toString().toLowerCase();

    return locationName.includes(searchText) || locationId.includes(searchText);
  }

  public scrollToSuggestions(route: InventoryTransferRouteModel): void {
    const routeSuggestions = this.suggestions.items.filter((s) => {
      return Number(s.fromLocationId) === Number(route.sourceLocationId) &&
             Number(s.toLocationId) === Number(route.destinationLocationId);
    });

    if (routeSuggestions.length > 0) {
      const suggestionsSection = document.querySelector('.suggestions-section');
      if (suggestionsSection) {
        suggestionsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  public scrollToRouteInfo(route: InventoryTransferRouteModel): void {
    const routesContainer = document.querySelector('.routes-container');
    if (routesContainer) {
      routesContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  public async fetchData(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      await Promise.all([
        this.suggestions.fetch(),
        this.locations.fetch({limit: 250}),
        this.routes.fetch(),
      ]);

    } catch (e: any) {
      this.error = 'Failed to load data: ' + e.message;
    } finally {
      this.loading = false;
    }
  }

  // Lifecycle methods
  public async mounted(): Promise<void> {
    // Track page view
    if (this.store) {
      this.eventHandler.track('Inventory Transfer Suggestions Page Viewed', {
        page: 'inventory_transfer_suggestions',
        has_locations: false,
        has_routes: false,
        total_locations: 0,
        total_routes: 0,
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Page Viewed');
    }

    await this.fetchData();

    // Track data loading completion with updated metrics
    if (this.store && this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Suggestions Data Loaded', {
        total_locations: this.locations.items.length,
        total_routes: this.routes.items.length,
        has_locations: this.locations.items.length > 0,
        has_routes: this.routes.items.length > 0,
        available_locations: this.availableLocations.length,
      });
    }
  }

  // ============================================================================
  // CSV EXPORT METHODS
  // ============================================================================

  /**
   * Export transfer suggestions to CSV file
   */
  public exportToCSV(): void {
    try {
      // Get data to export (all filtered or selected only)
      const dataToExport = this.getExportData();

      if (dataToExport.length === 0) {
        EventBus.$emit('show-snackbar', {
          message: 'No suggestions to export',
          color: 'warning',
          icon: 'mdi-alert',
        });
        return;
      }

      // Sort data based on view mode
      const sortedData = this.sortForExport(dataToExport);

      // Map to CSV format
      const csvRows = sortedData.map((item) => this.mapSuggestionToCSV(item));

      // Generate filename
      const filename = this.generateExportFilename();

      // Create CSV data structure
      const csvData = {
        headers: Object.keys(csvRows[0]),
        rows: csvRows.map((row) => Object.values(row)),
      };

      // Download CSV
      CSVExportUtil.downloadCSVData(csvData, filename);

      // Show success message
      EventBus.$emit('show-snackbar', {
        message: `Exported ${csvRows.length} suggestion${csvRows.length !== 1 ? 's' : ''} to CSV`,
        color: 'success',
        icon: 'mdi-download',
      });

      // Track export event
      if (this.eventHandler) {
        this.eventHandler.track('Transfer Suggestions Exported', {
          total_exported: csvRows.length,
          selection_mode: this.selectionMode,
          location_id: this.selectedLocationId,
          export_scope: this.hasSelectedItems ? 'selected' : 'all',
        });
      }
    } catch (error) {
      EventBus.$emit('show-snackbar', {
        message: 'Failed to export CSV. Please try again.',
        color: 'error',
        icon: 'mdi-alert-circle',
      });
    }
  }

  /**
   * Get data to export - selected items or all filtered suggestions
   */
  private getExportData(): any[] {
    // Flatten suggestions from all routes - items already have location IDs
    const allItems = this.filteredSuggestions.flatMap(route => route.suggestions);

    // If items are selected, filter to only selected items
    if (this.hasSelectedItems) {
      const selectedKeys = new Set(Object.keys(this.selectedItems));
      return allItems.filter((item) => {
        const itemKey = `${item.variantId}-${item.sourceLocationId}-${item.destinationLocationId}`;
        return selectedKeys.has(itemKey);
      });
    }

    // Otherwise, return all items
    return allItems;
  }

  /**
   * Sort export data based on view mode
   * Uses getLocationName() to get location names by ID
   */
  private sortForExport(data: any[]): any[] {
    return [...data].sort((a, b) => {
      // Primary sort: by location based on view mode
      let primaryCompare = 0;

      if (this.selectionMode === 'destination') {
        // Destination mode: sort by source location (from)
        const aLocation = this.getLocationName(a.sourceLocationId);
        const bLocation = this.getLocationName(b.sourceLocationId);
        primaryCompare = aLocation.localeCompare(bLocation);
      } else {
        // Source mode: sort by destination location (to)
        const aLocation = this.getLocationName(a.destinationLocationId);
        const bLocation = this.getLocationName(b.destinationLocationId);
        primaryCompare = aLocation.localeCompare(bLocation);
      }

      // If locations are same, sort by priority score (descending)
      if (primaryCompare === 0) {
        return (b.priorityScore || 0) - (a.priorityScore || 0);
      }

      return primaryCompare;
    });
  }

  /**
   * Map suggestion item to CSV row format
   * Uses getLocationName() to lookup location names by ID for accuracy
   */
  private mapSuggestionToCSV(item: any): Record<string, any> {
    return {
      'Product Title': item.productTitle || '',
      'Variant Title': item.variantTitle || '',
      'SKU': item.sku || '',
      'Vendor': item.vendor || '',
      'Barcode': item.barcode || '',
      'Unit Price': item.unitPrice || 0,
      'Total Price': item.totalPrice || 0,
      'Priority Level': this.getPriorityLevel(item.priorityScore || 0),
      'Priority Score': item.priorityScore || 0,
      'Source Location': this.getLocationName(item.sourceLocationId),
      'Source Inventory': item.sourceInventory || 0,
      'Destination Location': this.getLocationName(item.destinationLocationId),
      'Destination Inventory': item.destinationInventory || 0,
      'Suggested Quantity': item.suggestedQuantity || 0,
      'Lead Time (Days)': item.leadTimeDays || 0,
      'Has Pending Transfer': item.hasPendingTransfer ? 'Yes' : 'No',
    };
  }

  /**
   * Generate export filename with metadata
   */
  private generateExportFilename(): string {
    const mode = this.selectionMode === 'destination' ? 'to' : 'from';
    const locationName = this.selectedLocationId !== null
      ? this.getLocationName(this.selectedLocationId) || 'all'
      : 'all';
    const location = this.sanitizeFilename(locationName);
    const timestamp = this.formatDate(new Date(), 'YYYY-MM-DD');

    return `transfer_suggestions_${mode}_${location}_${timestamp}.csv`;
  }

  /**
   * Sanitize filename string
   */
  private sanitizeFilename(name: string): string {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Format date for filename or display
   */
  private formatDate(date: Date, format: string): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (format === 'YYYY-MM-DD') {
      return `${year}-${month}-${day}`;
    }

    return `${year}-${month}-${day}`;
  }
}
